/**
 * Services table (DNKADM-0079; single grouped table DNKADM-0089) — the `v2_service`
 * row rendering extracted from {@link ServicesScreen} so the SAME row renders in both
 * the flat list and the grouped-by-category view (re-house, don't duplicate; identical
 * behavior).
 *
 * ONE source of truth for a service row's cells: {@link ServiceRow} returns the
 * `<TableRow>` (the 9 shared columns) and {@link ServicesTableHead} the matching
 * `<TableHead>`. The flat fallback ({@link ServicesTable}) wraps the head + rows in its
 * own `<Table>`; the grouped view (DNKADM-0089) renders the head ONCE at the top of a
 * single table and emits the rows directly beneath each category's grouping row, so all
 * columns stay equal width across every category and the header never repeats.
 *
 * A row is name (link → detail; a star "Featured" glyph precedes the name on an
 * explicit `featured === true` — never on absent, DNKADM-0290) → credit bucket → staff/resource modes → taxable →
 * #variations → the lifecycle `status` chip (Draft/Active/Inactive, DNKADM-0140 — this
 * replaced the old isActive chip), with Copy (always) + a status-gated Publish (DRAFT)
 * / Deactivate (ACTIVE) / Reactivate (INACTIVE) action, then the trailing open
 * chevron. Opening the record is the row/name/chevron click → the detail route
 * (DNKADM-0284, the MembersList pattern); base-field editing lives on the detail page
 * (DNKADM-0286 retired the list quick-edit trigger). Every action button stops
 * propagation so it never triggers the row open. All data and callbacks are supplied
 * by the owning screen — this piece holds no state and fires no requests beyond the
 * row-open navigation (the grouped view stays a single list call, no per-row fan-out).
 *
 * **Sortable head (DNKADM-0430 P1-F).** Given `sort` + `onSort`, the head renders the shared
 * {@link SortableHeader} on every data column (the screen owns the state, in the URL);
 * without them it stays plain.
 */
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Chip,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, resolveTokens } from '@/theme';
import { assignmentModeLabel } from '@/domain/v2Enums';
import type { V2Service } from '@/api/services';
import { SortableHeader } from '@/components/list/SortableHeader';
import type { SortState } from '@/components/list/listSort';
import { serviceName, serviceStatusLabel, type ServiceSortKey } from './servicesListView';

/**
 * The number of columns a service row (and the shared head) spans. The grouped view's
 * full-width category grouping rows use this as their `colSpan` so they span the table.
 */
export const SERVICE_COLUMN_COUNT = 9;

/** Chip label/color/variant for a service's lifecycle status (DNKADM-0140). */
function statusChipProps(
  status: V2Service['status'],
): { label: string; color: 'warning' | 'success' | 'default'; variant: 'outlined' | 'filled' } {
  const label = serviceStatusLabel(status);
  if (status === 'DRAFT') return { label, color: 'warning', variant: 'outlined' };
  if (status === 'INACTIVE') return { label, color: 'default', variant: 'outlined' };
  return { label, color: 'success', variant: 'filled' };
}

/** The head's sort wiring (DNKADM-0430); omit it for a plain head. */
export interface ServicesTableSortProps {
  sort?: SortState<ServiceSortKey> | undefined;
  onSort?: ((key: ServiceSortKey) => void) | undefined;
}

/** The shared 8-column header row. Rendered ONCE per table (flat or grouped). */
export function ServicesTableHead({ sort, onSort }: ServicesTableSortProps = {}) {
  const column = (label: string, col: ServiceSortKey, align?: 'right') =>
    sort && onSort ? (
      <SortableHeader label={label} col={col} align={align} sort={sort} onSort={onSort} />
    ) : (
      <TableCell align={align}>{label}</TableCell>
    );
  return (
    <TableHead>
      <TableRow>
        {column('Name', 'name')}
        {column('Credit bucket', 'bucket')}
        {column('Staff', 'staff')}
        {column('Resources', 'resources')}
        {column('Taxable', 'taxable')}
        {column('Variations', 'variations', 'right')}
        {column('Status', 'status')}
        <TableCell align="right">Actions</TableCell>
        <TableCell sx={{ width: 44 }} aria-hidden />
      </TableRow>
    </TableHead>
  );
}

export interface ServiceRowProps {
  service: V2Service;
  /** Resolve a service's credit-bucket label ("Free" / bucket name / raw id). */
  bucketLabel: (service: V2Service) => string;
  onPublish: (service: V2Service) => void;
  onCopy: (service: V2Service) => void;
  onDeactivate: (service: V2Service) => void;
  onReactivate: (service: V2Service) => void;
}

/**
 * One service's `<TableRow>` (the 8 shared columns). The single row definition used by
 * both the flat table and the grouped body — keep all per-row markup/behavior here.
 */
export function ServiceRow({
  service,
  bucketLabel,
  onPublish,
  onCopy,
  onDeactivate,
  onReactivate,
}: ServiceRowProps) {
  const name = serviceName(service);
  const t = resolveTokens(useTheme());
  const navigate = useNavigate();
  const openDetail = () => navigate(`/services/${service.id}`);
  return (
    <TableRow hover onClick={openDetail} sx={{ cursor: 'pointer' }}>
      <TableCell>
        {/* Featured indicator (DNKADM-0290) — explicit `true` ONLY: `featured` is
            `boolean | undefined`, and absent means the API didn't send the field,
            which is NOT "not featured" (the DNKADM-0256 absent-signal discipline). */}
        {service.featured === true && (
          <Icon
            name="star"
            title="Featured"
            sx={{ mr: 0.5, fontSize: 16, verticalAlign: 'text-bottom', color: t.brand }}
          />
        )}
        <MuiLink
          component={RouterLink}
          to={`/services/${service.id}`}
          onClick={(event) => event.stopPropagation()}
        >
          {name}
        </MuiLink>
      </TableCell>
      <TableCell>{bucketLabel(service)}</TableCell>
      <TableCell>{assignmentModeLabel(service.staffAssignment)}</TableCell>
      <TableCell>{assignmentModeLabel(service.resourceAssignment)}</TableCell>
      <TableCell>{service.taxable ? 'Yes' : 'No'}</TableCell>
      <TableCell align="right">{service.variationCount ?? '—'}</TableCell>
      <TableCell>
        <Chip size="small" {...statusChipProps(service.status)} />
      </TableCell>
      <TableCell align="right">
        <IconAction
          size="small"
          label={`Duplicate service ${name}`}
          onClick={(event) => {
            event.stopPropagation();
            onCopy(service);
          }}
        >
          <Icon name="content_copy" />
        </IconAction>
        {service.status === 'DRAFT' && (
          <IconAction
            size="small"
            label={`Publish service ${name}`}
            onClick={(event) => {
              event.stopPropagation();
              onPublish(service);
            }}
          >
            <Icon name="publish" />
          </IconAction>
        )}
        {service.status === 'ACTIVE' && (
          <IconAction
            size="small"
            label={`Deactivate service ${name}`}
            onClick={(event) => {
              event.stopPropagation();
              onDeactivate(service);
            }}
          >
            <Icon name="toggle_off" />
          </IconAction>
        )}
        {service.status === 'INACTIVE' && (
          <IconAction
            size="small"
            label={`Reactivate service ${name}`}
            onClick={(event) => {
              event.stopPropagation();
              onReactivate(service);
            }}
          >
            <Icon name="toggle_on" />
          </IconAction>
        )}
      </TableCell>
      <TableCell align="center" sx={{ color: t.textGhost }}>
        <IconAction
          size="small"
          label={`Open ${name}`}
          onClick={(event) => {
            event.stopPropagation();
            openDetail();
          }}
        >
          <Icon name="chevron_right" />
        </IconAction>
      </TableCell>
    </TableRow>
  );
}

export interface ServicesTableProps extends ServicesTableSortProps {
  services: V2Service[];
  /** Resolve a service's credit-bucket label ("Free" / bucket name / raw id). */
  bucketLabel: (service: V2Service) => string;
  /** Accessible label for the `<table>` (the flat fallback view). */
  ariaLabel: string;
  onPublish: (service: V2Service) => void;
  onCopy: (service: V2Service) => void;
  onDeactivate: (service: V2Service) => void;
  onReactivate: (service: V2Service) => void;
}

/**
 * The flat (ungrouped) fallback table — a complete `<Table>` (its own head + the shared
 * {@link ServiceRow}s). Used when the inline `categories` field is absent on every row.
 */
export function ServicesTable({
  services,
  bucketLabel,
  ariaLabel,
  onPublish,
  onCopy,
  onDeactivate,
  onReactivate,
  sort,
  onSort,
}: ServicesTableProps) {
  return (
    <TableContainer>
      <Table aria-label={ariaLabel} size="small">
        <ServicesTableHead sort={sort} onSort={onSort} />
        <TableBody>
          {services.map((service) => (
            <ServiceRow
              key={service.id}
              service={service}
              bucketLabel={bucketLabel}
              onPublish={onPublish}
              onCopy={onCopy}
              onDeactivate={onDeactivate}
              onReactivate={onReactivate}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
